/**
 * Quote Comparison workbench — prototype `vCompare()`, contract §8, infra C.5.
 *
 * Where the purchaser decides who supplies what. One `GET comparison.get_comparison` returns
 * the offer matrix, the FX snapshot and the last saved allocation (if any); everything after
 * that is computed on the client — AED conversion, best-per-item, the four strategy previews,
 * coverage and the manual split — because each recomputes on every
 * click (contract §3: "no allocation math server-side").
 *
 * The only thing written back is the chosen allocation, as a flat `AllocationRow[]` — several
 * rows may share an item_code, which is the multi-supplier split (Task 10's whole point).
 */

import { useCompanyBlanketOrders } from '../../lib/api/useCompanyBlanketOrders'
import { printedDescription, withoutInternalAnnotations } from '../../lib/internalNotes'
import { useEffect, useMemo, useRef, useState, type ComponentProps } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { Badge } from '../../components/Badge'
import { ReadOnlyGate } from '../../components/ReadOnlyGate'
import { lockReason } from '../../lib/readOnly'
import { ContextBar } from '../../components/ContextBar'
import { Money } from '../../components/Money'
import { Button, Card, PageHead, Placeholder } from '../../components/ui'
import { Modal } from '../../components/Modal'
import { useToast } from '../../components/Toast'
import { useEnquiry } from '../../lib/api/useEnquiries'
import { useBlanketOrderItems, useItemPriceRows, useItemsByCode, useStockByCode } from '../../lib/api/useItems'
import { useDeliveryLocationPriceLists } from '../../lib/api/useMasters'
import { inHousePriceFor, type InHousePrice } from '../../lib/inHousePrice'
import { buildRateIndex, contractsForItem } from '../../lib/blanketOrders'
import { IN_HOUSE_SUPPLIER } from '../../lib/directPricing'
import { editedDescription } from '../../lib/rfq'
import { ItemHistoryModal } from '../rfq/ItemHistoryModal'
import { priceFromStock } from '../../lib/directPricing'
import { useComparison, useSaveAllocation } from '../../lib/api/useComparison'
import { useUpdateEnquiry, useUpdateLine } from '../../lib/api/useEnquiryMutations'
import { useCreateQuotation, useExistingQuotation, useQuotationNameForEnquiry, useSetLineCosts } from '../../lib/api/useQuotation'
import { usePricingRules } from '../../lib/api/useSettings'
import { buildQuotationPayload } from '../../lib/quotationFromAllocation'
import { buildContractQuotationLines, isContractPriced } from '../../lib/contractQuotation'
import { describeOutliers, detectPriceOutliers, supplierTotals } from '../../lib/priceOutliers'
import { formatMoney } from '../../lib/format'
import { convert } from '../../lib/fx'
import type { DisplayCurrency } from '../../lib/displayCurrency'
import { baseCurrencyFor } from '../../lib/api/baseCurrency'
import { describeRate, isStale, pickRate, toWireFx } from '../../lib/exchangeRates'
import { useCompanyCurrencies, useCustomerCurrency, useExchangeRates } from '../../lib/api/useMasters'
import {
  autoCoverItem,
  collectUomNotes,
  computeAllocation,
  normaliseComparison,
  previewStrategy,
  pruneAllocations,
  rowsForItem,
  setAllocationQty,
  summariseAll,
  toggleAllocation,
  pickSoleSupplier,
  withItemRows,
  STRATEGIES,
  type Strategy,
  type StrategyPreview,
} from '../../lib/compare'
import { convertAllocationsToBase } from '../../lib/allocationCurrency'
import { StrategyBar } from './StrategyBar'
import { WorkbenchTable } from './WorkbenchTable'
import { RankedTable } from './RankedTable'
import { UomNotesPanel } from './UomNotesPanel'
import { QuotationReuseWarning } from './QuotationReuseWarning'
import { revisionRepriceChanges, type RepriceRow } from '../../lib/revisionReprice'
import { completeQuotationPayload } from '../../lib/quotationPayload'
import { describeMandatory, enquiryHeaderGaps } from '../../lib/quotationMandatory'
import {
  describeFloorBreaches,
  floorFromRules,
  linesBelowFloor,
  type FloorBreach,
} from '../../lib/marginFloor'
import type { CreateQuotationPayload } from '../../lib/quotationFromAllocation'
import type { AllocationRow, Currency, EnquiryItem, Money as MoneyValue } from '../../lib/types'

export function ComparePage() {
  const { id = '' } = useParams()
  const navigate = useNavigate()
  const toast = useToast()

  const [strategy, setStrategy] = useState<Strategy>('cheapest')
  const [search, setSearch] = useState('')
  const [hiddenSuppliers, setHiddenSuppliers] = useState<string[]>([])
  const [allocations, setAllocations] = useState<AllocationRow[] | null>(null)
  /*
    ONE supplier per item, on by default for this company.

    Splitting a line across suppliers is the exception, not the rule — it exists for covering stock
    that no single supplier holds. With it always on, ticking a second supplier APPENDED a row and
    left the first holding most of the quantity, which reads as the click being ignored. Default
    on, and switchable off for the enquiries that genuinely need a split.

    Session state, not a stored setting: there is no company-level field for it on the wire, and
    inventing one that silently failed to persist would be worse than a control that resets.
  */
  const [soleSupplier, setSoleSupplier] = useState(true)
  /*
    Ranked (2026-11-03): each row lists only the suppliers who quoted it, cheapest first — at 300
    items × 20 suppliers the matrix is mostly dashes scrolled past sideways. The matrix stays one
    click away, unchanged, for anyone who wants the old grid.
  */
  const [view, setView] = useState<'ranked' | 'matrix'>('ranked')
  // Set only when `build_quotation` REUSED an existing quotation rather than rebuilding it from
  // this allocation (`buildQuotationOutcome`) — non-null blocks the auto-navigate and renders
  // `QuotationReuseWarning` next to the button instead. Cleared on open-anyway or on any further
  // allocation edit, never on a timer — see that component's header for why a toast is wrong here.
  const [reuseWarning, setReuseWarning] = useState<{ quotation: string; rows: RepriceRow[] } | null>(null)
  /*
    Acknowledged the excluded offers once, for this allocation.

    An offer dropped for a UOM the item master cannot convert means a supplier QUOTED and is
    missing from the ranking — the cheapest row on screen may not be the cheapest offer received.
    Pricing a customer quotation from an incomplete comparison is the expensive mistake, and it is
    silent, so it is confirmed once rather than merely noted in a panel.
  */
  const [uomAcknowledged, setUomAcknowledged] = useState(false)

  const enquiryQuery = useEnquiry(id)
  const comparisonQuery = useComparison(id)

  const comparison = comparisonQuery.data
  // No `.base` on `Comparison.fx` any more (it's `WireFx` — pairs, not a base-labelled map);
  // ranking and display both settle on the company's base currency, same as every other screen.
  // Published by `usePricingRules` — see `baseCurrency.ts`. Falls back only until it resolves.
  /*
    THIS ENQUIRY's company currency, not one app-wide value. Bosun is multi-company and currency
    follows company — seven companies across AED / SAR / INR / BHD, measured 2026-08-08. Labelling
    a SAR company's workbench in AED reads as a correct figure and is not one.

    Falls back to the app-wide value for an enquiry with no company, which is every enquiry created
    from a mail until `create_enquiry` accepts the field (`E11`).
  */
  useCompanyCurrencies()
  const exchangeRates = useExchangeRates()
  const customerCurrency = useCustomerCurrency(enquiryQuery.data?.header.customer)

  const baseCurrency = baseCurrencyFor(enquiryQuery.data?.header.company) as Currency

  /*
    What the customer will actually be billed in, and how old the rate is.

    Shown on the page rather than only in a toast when the quotation is built: the newest rate on
    this bench is 16 days old and several are from 2022, and a converted total that does not show
    its rate's date invites nobody to question it. Stale rates get a warning tone; a missing rate
    says so plainly, because that one blocks the quotation entirely.
  */
  // The enquiry's OWN choice first (RFQ header card, 2026-09-14), then the customer's currency on
  // file. Empty means the company's currency.
  const chosenCurrency = (enquiryQuery.data?.header.customer_currency || customerCurrency.data || '').trim()
  /*
    The currency the quotation will be BUILT in, chosen here (Akhil, 2026-09-15: "no option to
    change currency in the quote comparison page"). It writes the enquiry's own `customer_currency`
    — the same field the RFQ header card edits — so every screen agrees, and `buildQuotationPayload`
    below reads it back through `chosenCurrency`. Offered: the company's currency, then every
    currency we hold a rate FROM it to, because a currency with no rate cannot be quoted in.
  */
  const updateEnquiry = useUpdateEnquiry(id, {
    onSuccess: (after) => toast(`Quotation will be built in ${after.header.customer_currency || baseCurrency}`),
    onError: (error) => toast(`Could not set the currency: ${error.message}`),
  })
  const quoteCurrencyOptions = useMemo(() => {
    const reachable = (exchangeRates.data ?? [])
      .filter((row) => row.from_currency?.trim().toUpperCase() === baseCurrency)
      .map((row) => row.to_currency?.trim().toUpperCase())
      .filter((c): c is string => !!c && c !== baseCurrency)
    const extra = chosenCurrency && chosenCurrency !== baseCurrency && !reachable.includes(chosenCurrency) ? [chosenCurrency] : []
    return [baseCurrency, ...[...new Set([...reachable, ...extra])].sort()]
  }, [exchangeRates.data, baseCurrency, chosenCurrency])

  /*
    Figures on this page are COSTED in the company's currency and SHOWN in the customer's once
    one is chosen above — every cell, strategy card, supplier total and the total card, through
    `Money`'s display conversion, so a purchaser who picked SAR sees SAR. `undefined` (same
    currency, or no rate) shows everything as costed; the FX banner below says which.
  */
  const display = useMemo((): DisplayCurrency | undefined => {
    if (!chosenCurrency || chosenCurrency === baseCurrency) return undefined
    const fx = toWireFx(exchangeRates.data ?? [])
    return fx[`${baseCurrency}_${chosenCurrency}`] ? { currency: chosenCurrency as Currency, fx } : undefined
  }, [chosenCurrency, baseCurrency, exchangeRates.data])
  const shown = (amountInBase: number): MoneyValue =>
    display ? convert({ amount: amountInBase, currency: baseCurrency }, display.currency, display.fx) : { amount: amountInBase, currency: baseCurrency }

  const quoteFx = useMemo(() => {
    const to = chosenCurrency
    if (to === '' || to === baseCurrency) return null
    const picked = pickRate(exchangeRates.data ?? [], baseCurrency, to)
    const now = new Date()
    return picked === null
      ? { to, missing: true as const, note: '', stale: false }
      : { to, missing: false as const, note: describeRate(picked, now), stale: isStale(picked.date, now) }
  }, [chosenCurrency, exchangeRates.data, baseCurrency])

  // Supplier identity is CODE throughout (`ComparisonSupplier.supplier`, task 8b); this map is
  // the only place a display name is looked up, for rendering — never for keying allocations.
  const supplierName = useMemo(() => {
    const map = new Map<string, string>()
    for (const s of comparison?.suppliers ?? []) map.set(s.supplier, s.supplier_name || s.supplier)
    return map
  }, [comparison])
  const supplierCodes = useMemo(() => (comparison?.suppliers ?? []).map((s) => s.supplier), [comparison])

  // Item master supplies the UOM conversions needed to normalise a supplier who quoted in a
  // different unit than the RFQ asked for.
  const itemCodes = useMemo(() => comparison?.items.map((i) => i.item_code) ?? [], [comparison])
  const { byCode: itemsByCode, isResolved: masterResolved } = useItemsByCode(itemCodes)

  /*
    ---- "Price this line" — our stock, a contract, or a past supplier price (Akhil, 2026-09-15) ----

    The fresh offers are the matrix; these are the ALTERNATIVES to it, shown in the same dialog
    the RFQ page uses so the purchaser weighs all of them together. A choice here writes the
    ENQUIRY line exactly as the RFQ page does (route `contract`, `selected_rate`, the source as
    supplier) — never a comparison allocation, because `Comparison Allocation.supplier` is a Link
    and refuses "In-house stock" (measured: LinkValidationError). The line then leaves the
    matrix for the "Priced directly" strip and the quotation build already carries it.
  */
  const [pricingRow, setPricingRow] = useState<string | null>(null)
  const stockByCode = useStockByCode(itemCodes, true)
  const priceRows = useItemPriceRows(itemCodes)
  const priceLists = useDeliveryLocationPriceLists()
  // This enquiry's company's contracts only (2026-10-26).
  const blanketOrders = useCompanyBlanketOrders(enquiryQuery.data?.header.company)
  const allOrderNames = useMemo(() => (blanketOrders.data ?? []).map((o) => o.name), [blanketOrders.data])
  const allBlanketItems = useBlanketOrderItems(allOrderNames)
  const allRateIndex = useMemo(() => buildRateIndex(allBlanketItems.data), [allBlanketItems.data])
  const updateLine = useUpdateLine(id, {
    onError: (error) => toast(`Could not price the line: ${error.message}`),
  })

  /*
    The ENQUIRY's own lines, by row number — for the words on each row and the description the
    quotation prints. `get_comparison` carries neither: its `items` have `item_name` only, so the
    "customer's own wording" this screen showed was the catalogue name again, and an edit made on
    the RFQ page (description, 2026-09-14) never appeared here or on the quotation built from
    here. `useEnquiry` is the same cache the RFQ page writes into, so this is current.
  */
  const enquiryLineByRow = useMemo(
    () => new Map((enquiryQuery.data?.items ?? []).map((line) => [line.cust_sno, line])),
    [enquiryQuery.data],
  )
  const preferredPriceList =
    enquiryQuery.data?.header.price_list ??
    (enquiryQuery.data?.header.delivery_location ? (priceLists.data?.get(enquiryQuery.data.header.delivery_location) ?? null) : null)
  /** Our own price for the line's unit — see `lib/inHousePrice.ts`. */
  const ourPriceFor = (line: EnquiryItem): InHousePrice | null => {
    const item = line.item_code ? itemsByCode.get(line.item_code) : undefined
    if (!item || !line.item_code) return null
    return inHousePriceFor(priceRows.data?.get(line.item_code), item, line.uom, preferredPriceList)
  }
  // Lines already priced directly (stock / contract / past price) — out of the matrix, into the strip.
  const pricedDirectly = useMemo(
    () => (enquiryQuery.data?.items ?? []).filter(isContractPriced),
    [enquiryQuery.data],
  )
  const pricedDirectlyRows = useMemo(() => new Set(pricedDirectly.map((l) => l.cust_sno)), [pricedDirectly])

  const normalised = useMemo(
    () =>
      comparison
        ? normaliseComparison(comparison.items, itemsByCode, comparison.fx, baseCurrency).filter((item) => !pricedDirectlyRows.has(item.enquiry_row_no)).map((item) => {
            const source = enquiryLineByRow.get(item.enquiry_row_no)
            if (!source) return item
            // The CUSTOMER's words, verbatim — never the editable description (Akhil, 2026-09-15).
            // An edited description is shown on its own line beneath, by `WorkbenchTable`.
            // Our notes ("(customer's unit: …)", "(code … not in catalogue)") stay on the RFQ page.
            const wording = withoutInternalAnnotations(source.raw_text)
            return wording
              ? { ...item, lines: item.lines.map((l) => ({ ...l, description: wording, edited: editedDescription(source) || undefined })) }
              : item
          })
        : [],
    [comparison, itemsByCode, baseCurrency, enquiryLineByRow, pricedDirectlyRows],
  )

  /**
   * Seed the working allocation once ALL the data is in: the server's last saved split
   * (`comparison.allocations`, part of the same GET this screen already loaded) if there is
   * one, otherwise the current strategy's suggestion. Editing afterwards is local.
   *
   * The item-master guard is load-bearing. Without it this runs while `itemsByCode` is still
   * empty, every UOM-converted offer is treated as unconvertible, and the initial allocation
   * is computed with those suppliers missing — a wrong split that is never recomputed,
   * because `allocations` is non-null from then on.
   */
  useEffect(() => {
    if (allocations !== null || normalised.length === 0 || !masterResolved) return

    const saved = comparison?.allocations ?? []
    if (saved.length > 0) {
      // A saved split may name a supplier who has since stopped quoting, or a line the
      // comparison no longer carries; drop them so every downstream number agrees.
      const { allocations: cleaned, dropped } = pruneAllocations(normalised, saved)
      setAllocations(cleaned)
      if (dropped.length > 0) {
        toast(`${dropped.length} saved allocation(s) dropped — supplier no longer quoting`)
      }
      return
    }
    setAllocations(computeAllocation(normalised, strategy))
  }, [allocations, normalised, comparison, strategy, masterResolved, toast])

  const saveAllocation = useSaveAllocation(id, {
    // Lets the save advance the enquiry to `Comparison Done` — the server does not (ask `D13`),
    // so without this the Requirements board still reads `RFQ Sent` for everyone. Gated against
    // moving a quoted or decided enquiry backwards; see `lib/statusFlow.ts`.
    currentStatus: enquiryQuery.data?.status,
    onError: (error) => toast(`Could not save the allocation: ${error.message}`),
  })
  /* The quotation is priced HERE and created whole — `comparison.build_quotation` cannot run
     (`comparison.py:924` imports one `quoflow` too many). `useBuildQuotation` is therefore not
     called at all any more; the reuse check it used to provide is now ours. */
  const pricingRules = usePricingRules()
  /*
    The minimum margin every line must clear before a quotation can be created (`marginFloor.ts`).
    Both halves come from Settings: the rule supplies the number, `enforce` decides whether it
    bites at all — which is how the feature is switched off.
  */
  const marginFloor = floorFromRules(pricingRules.data?.rules ?? [])
  const enforceMargin = (pricingRules.data?.enforce ?? false) && marginFloor !== undefined
  /** Set when creation was stopped by the floor; holds what to create if the purchaser overrides. */
  const [floorPrompt, setFloorPrompt] = useState<{ breaches: FloorBreach[]; payload: CreateQuotationPayload } | null>(
    null,
  )
  /** One-shot: consumed by the next create, so an override never outlives the decision. */
  const [overrideFloor, setOverrideFloor] = useState(false)
  const existingQuotation = useQuotationNameForEnquiry(id)
  // The sent document's lines, to diff a new allocation against; the per-line writer that
  // re-prices them (the revision page's own). See the sent-quotation branch of `buildQuotation`.
  const sentQuotation = useExistingQuotation(existingQuotation.data?.sent_at ? id : null)
  const setLineCosts = useSetLineCosts(id, existingQuotation.data?.name, { onError: (error) => toast(`Could not re-price: ${error.message}`) })
  const savedComparisonName = useRef('')
  const createQuotation = useCreateQuotation({
    onSuccess: (name) => {
      toast(`Quotation ${name} priced from this allocation`)
      navigate(`/quote/${id}`)
    },
    onError: (error) => toast(`Could not build the quotation: ${error.message}`),
  })

  const working = allocations ?? []

  /*
    "Convert all the rates" (Akhil, 2026-09-14). Offered only while some allocated row is still in
    a supplier's currency; one click restates every such row into the company's at the rate on
    file, so the sheet reads in one currency and the quotation is built from exactly the figures
    on screen. `convertAllocationsToBase` keeps the supplier's own figure and currency for the PO.
  */
  const foreignRows = working.filter((row) => (row.currency || baseCurrency).toUpperCase() !== baseCurrency.toUpperCase())
  const convertAllRates = () => {
    const result = convertAllocationsToBase(working, baseCurrency, exchangeRates.data ?? [])
    if (result.converted > 0) setAllocations(result.rows)
    const parts: string[] = []
    if (result.converted > 0) parts.push(`${result.converted} rate${result.converted === 1 ? '' : 's'} converted to ${baseCurrency}`)
    if (result.missing.length > 0) parts.push(`no rate on file for ${result.missing.join(', ')} → ${baseCurrency} — those rows are unchanged`)
    toast(parts.join('; ') || `Every rate is already in ${baseCurrency}`)
  }

  const previews = useMemo(() => {
    const out = {} as Record<Strategy, StrategyPreview>
    for (const s of STRATEGIES) out[s] = previewStrategy(normalised, s)
    return out
  }, [normalised])

  const visibleSuppliers = supplierCodes.filter((s) => !hiddenSuppliers.includes(s))

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase()
    if (!q) return normalised
    // Searches what the customer wrote too — a purchaser looking for "gear oil" is often
    // reading from the customer's mail, not from our item master.
    return normalised.filter((item) =>
      `${item.name}${item.item_code}${item.lines.map((l) => `${l.cust_sno}${l.description}`).join('')}`
        .toLowerCase()
        .includes(q),
    )
  }, [normalised, search])

  const totals = summariseAll(normalised, working)
  const uomNotes = useMemo(() => collectUomNotes(normalised), [normalised])
  /*
    Point 27 — a supplier price that is probably a typo. Compared against the median of the other
    offers on the SAME line, because a fixed threshold is meaningless across 18,836 items. See
    `priceOutliers.ts` for why three offers are needed before anything is said.
  */
  const outliers = useMemo(() => detectPriceOutliers(normalised), [normalised])
  /* Point 26 — what each supplier is actually being given. The screen totalled the whole
     allocation but never split it, so "who is getting most of this order" could not be read. */
  const bySupplierTotals = useMemo(() => supplierTotals(working), [working])

  /* ---------- interactions ---------- */

  const applyStrategy = (next: Strategy) => {
    setStrategy(next)
    setAllocations(computeAllocation(normalised, next))
    setReuseWarning(null)
  }

  const handleToggle = (itemCode: string, rowNo: string, supplier: string) => {
    const item = normalised.find((i) => i.item_code === itemCode && i.enquiry_row_no === rowNo)
    if (!item) return
    const rows = rowsForItem(working, item)
    // `pickSoleSupplier` REPLACES; `toggleAllocation` splits. See both functions' own notes.
    const next = soleSupplier ? pickSoleSupplier(item, supplier, rows) : toggleAllocation(item, supplier, rows)
    setAllocations(withItemRows(working, item, next))
    setReuseWarning(null)
  }

  const handleSetQty = (itemCode: string, rowNo: string, supplier: string, qty: number) => {
    const item = normalised.find((i) => i.item_code === itemCode && i.enquiry_row_no === rowNo)
    if (!item) return
    const next = setAllocationQty(item, rowsForItem(working, item), supplier, qty)
    setAllocations(withItemRows(working, item, next))
    setReuseWarning(null)
  }

  const handleAutoCover = (itemCode: string, rowNo: string) => {
    const item = normalised.find((i) => i.item_code === itemCode && i.enquiry_row_no === rowNo)
    if (!item) return
    setAllocations(withItemRows(working, item, autoCoverItem(item)))
    setReuseWarning(null)
    toast(`Auto-covered ${item.name}`)
  }

  /**
   * Save the allocation, then create the quotation FROM IT, in the browser.
   *
   * `comparison.build_quotation` is unusable — it imports `quoflow.quoflow.quoflow.quoflow.…`
   * (`comparison.py:924`), the same one-segment-too-many defect as `B1`/`B2`. `create_quotation`
   * works, so the payload is priced here and sent whole. See `lib/quotationFromAllocation.ts` for
   * what that moves into the client and why it is deliberate.
   */
  const buildQuotation = () =>
    saveAllocation.mutate(working, {
      onSuccess: (saved) => {
        toast('Allocation saved')
        savedComparisonName.current = saved.comparison
        /*
          A quotation already SENT is not rewritten under the customer — changing what they were
          quoted after the fact is a revision, with its own flow. Warn, and let opening it be a
          deliberate choice.

          A DRAFT is re-priced in place from the split just saved. That is the whole point: the
          old flow could only warn, because `build_quotation` never rebuilt an existing document,
          so a purchaser who changed the allocation was told their change would not be reflected
          and given no way to reflect it.
        */
        /*
          Excluded offers first — before anything is written.

          `collectUomNotes` marks an offer `excluded` when its unit could not be restated into the
          RFQ's (no conversion in the item master) or its currency has no FX rate. Either way that
          supplier is absent from the ranking the allocation was chosen from.
        */
        const droppedForUom = uomNotes.filter((n) => n.handled === 'excluded')
        if (droppedForUom.length > 0 && !uomAcknowledged) {
          setUomAcknowledged(true)
          toast(
            `${droppedForUom.length} offer${droppedForUom.length === 1 ? ' was' : 's were'} left out of the ranking ` +
              `(unit or currency could not be converted) — check the UOM panel, then press again to quote.`,
          )
          return
        }

        const existing = existingQuotation.data
        setReuseWarning(null)
        /*
          Which enquiry rows the contract path will price. Read off the ENQUIRY, because the
          comparison's own items carry neither `route` nor `selected_rate`.
        */
        const contractPricedRows = new Set(
          enquiry.items.filter(isContractPriced).map((l) => l.cust_sno),
        )
        const built = buildQuotationPayload({
          enquiryId: id,
          customer: enquiry.header.customer,
          customerName: enquiry.header.customer,
          salesPerson: enquiry.header.sales_person,
          purchaser: enquiry.header.purchaser,
          vessel: enquiry.header.vessel ?? '',
          deliveryLocation: enquiry.header.delivery_location ?? '',
          deliveryPort: enquiry.header.delivery_port ?? '',
          transactionDate: new Date().toISOString().slice(0, 10),
          today: new Date(),
          // The customer's own currency, when they have one on file. Empty means quote in the
          // company's currency — which is the honest default, not a guess.
          customerCurrency: chosenCurrency,
          rates: exchangeRates.data ?? [],
          comparisonName: savedComparisonName.current,
          baseCurrency,
          settings: pricingRules.data ?? { enforce: false, rules: [] },
          lines: normalised.map((item) => ({
            item_code: item.item_code,
            item_name: item.name,
            enquiry_row_no: item.enquiry_row_no,
            uom: item.uom,
            // What prints under the name — the RFQ page's edit, else the customer's own words.
            description: (() => { const l = enquiryLineByRow.get(item.enquiry_row_no); return l ? printedDescription(l) || undefined : undefined })(),
            // The customer's own quantity, carried so a line nobody allocated still reaches the
            // quotation with something to price against.
            qty: item.qty,
            /*
              Priced below by `buildContractQuotationLines`, so it must NOT also be zero-filled by
              the allocation payload — that is what put an already-decided item on the quotation
              twice, once at 0.00. Both sides read `isContractPriced` so the two decisions cannot
              drift apart. Keyed on `cust_sno`, which IS the wire's `enquiry_row_no`.
            */
            pricedByContract: contractPricedRows.has(item.enquiry_row_no),
          })),
          allocations: working,
          // Carries each column's display name and the `Supplier Quotation` it came from, so every
          // leg of a split is recorded on the line — see `lib/sourcingNote.ts`.
          suppliers: comparison?.suppliers ?? [],
        })
        if (!built.ok) {
          toast(built.reason)
          return
        }
        /*
          The enquiry's CONTRACT lines travel with the allocated ones — always.

          `update_quotation` replaces `items` WHOLESALE, and the RFQ page can already have created
          a quotation from the blanket-order-priced lines (most provision enquiries are entirely
          contract-priced). Building only from the allocation would therefore not "add" the RFQ
          lines, it would DELETE the contract ones — silently, on a document the customer is about
          to receive.

          So whichever screen writes last produces the whole document. Priced by the same function
          the RFQ page uses, with the same margin and the same FX, so one quotation can never be
          priced two ways depending on which screen happened to save it.
        */
        const contractLines = buildContractQuotationLines(
          enquiry.items,
          new Map([...itemsByCode].map(([code, item]) => [code, item.name])),
          pricingRules.data ?? { enforce: false, rules: [] },
          built.conversion.rate,
        )
        if (contractLines.length > 0) {
          const round2 = (n: number) => Math.round(n * 100) / 100
          built.payload.items = [...built.payload.items, ...contractLines]
          const net = round2(built.payload.items.reduce((sum, i) => sum + i.amount, 0))
          built.payload.net_total = net
          built.payload.total = net
          built.payload.grand_total = net
        }
        /*
          Say what rate the customer is being quoted at, and how old it is. The bench's newest rate
          is 16 days old and several are from 2022 — a converted total that does not show its rate's
          date invites nobody to question it.
        */
        if (built.conversion.from !== built.conversion.to) {
          toast(`Quoted in ${built.conversion.to} at ${built.conversion.rate} — ${built.conversion.note}`)
        }

        /*
          ---- The minimum margin, checked HERE, at creation ----

          Not only at send. A quotation built below the floor and caught on the way out is work
          already done wrongly — the purchaser has to come back through the comparison and
          re-allocate. Catching it now means the choice is made where the prices are.

          `enforce` off, or no `minMarkup` rule, and this is inert: the feature is disabled
          exactly as Settings says it is.

          It BLOCKS but does not forbid. A customer negotiating a discount is a legitimate reason
          to go under, so the same dialog offers to proceed — named lines and numbers on screen, a
          deliberate second click. Hard, but not immovable.
        */
        const breaches = enforceMargin ? linesBelowFloor(built.payload.items, marginFloor) : []
        if (breaches.length > 0 && !overrideFloor) {
          setFloorPrompt({ breaches, payload: built.payload })
          return
        }

        /*
          A quotation already SENT is not rewritten under the customer (Akhil, 2026-09-17: "main
          changes can only be done in the revision page"). The lines just built are diffed against
          the document's and the banner offers to write only the changed ones, per line, as a
          revision — `revisionReprice.ts`. Nothing is written until that button is pressed.
        */
        if (existing && existing.sent_at !== null) {
          // Completed first, so a contract line carries its PO currency here as it does at creation.
          const rows = revisionRepriceChanges(sentQuotation.data?.lines ?? [], completeQuotationPayload(built.payload, { baseCurrency, company: enquiry.header.company, enquiryType: enquiry.header.enquiry_type }).items)
          setReuseWarning({ quotation: existing.name, rows })
          return
        }

        createQuotation.mutate({ enquiryId: id, payload: built.payload, existing: existing?.name ?? null, customerReferenceNo: enquiry.header.customer_reference_no, company: enquiry.header.company, enquiryType: enquiry.header.enquiry_type })
      },
    })

  /* ---------- states ---------- */

  // The item master is part of "loaded" here — the workbench cannot rank a converted offer
  // without its conversion factor, so showing the matrix early would show it wrong.
  if (enquiryQuery.isPending || comparisonQuery.isPending || !masterResolved) {
    return <Placeholder>Loading comparison…</Placeholder>
  }

  if (enquiryQuery.error || !enquiryQuery.data) {
    const error = enquiryQuery.error as Error | null
    return (
      <Placeholder>
        <b>Could not load {id}.</b> Check the mock backend is running (<b>npm run dev:all</b>).
        {error ? <div style={{ marginTop: 6 }}>{error.message}</div> : null}
      </Placeholder>
    )
  }

  const enquiry = enquiryQuery.data

  if (comparisonQuery.error) {
    return (
      <>
        <ContextBar enquiry={enquiry} active="compare" />
        <Placeholder>
          <b>Could not load the comparison matrix.</b>
          <div style={{ marginTop: 6 }}>{(comparisonQuery.error as Error).message}</div>
        </Placeholder>
      </>
    )
  }

  const saving = saveAllocation.isPending || createQuotation.isPending

  return (
    <>
      <ContextBar enquiry={enquiry} active="compare" />
      {/* Every control below is disabled once the enquiry is decided — `lib/readOnly.ts`. */}
      <ReadOnlyGate reason={lockReason(enquiry.status, 'compare')}>

      {/* The quotation this page builds needs a complete enquiry header (2026-09-17). Said up
          front, with the way back, rather than as a failure on the build button. */}
      {enquiryHeaderGaps(enquiry.header).length > 0 ? (
        <div className="banner" style={{ background: 'var(--warnbg)', borderColor: 'var(--warn)', marginBottom: 10 }}>
          ⚠ The quotation cannot be built until the enquiry header has <b>{describeMandatory(enquiryHeaderGaps(enquiry.header))}</b>.{' '}
          <span className="linkish" onClick={() => navigate(`/rfq/${id}`)}>
            Fill them on the RFQ builder →
          </span>
        </div>
      ) : null}
      <PageHead help="compare"
        title="Quote Comparison — workbench"
        subtitle={
          <>
            Offers per item across all suppliers (compared in {baseCurrency}
            {display ? <>, shown in <b>{display.currency}</b></> : null}). Click a cell to source from that
            supplier — pick <b>two or more per item</b> to cover the quantity.
          </>
        }
      />

      {normalised.length === 0 ? (
        <Card style={{ textAlign: 'center', padding: 34, borderStyle: 'dashed' }}>
          <div style={{ fontSize: 28 }}>⚖️</div>
          <div style={{ fontWeight: 800, margin: '8px 0 4px' }}>Nothing to compare yet</div>
          <div style={{ color: 'var(--muted)', fontSize: 13 }}>
            Comparison needs RFQ-routed lines with supplier quotations against them. Check the{' '}
            <span className="linkish" onClick={() => navigate(`/suppliers/${id}`)}>
              supplier replies
            </span>
            .
          </div>
        </Card>
      ) : (
        <>
          <div className="banner" style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
            <span>💱 Quote this customer in</span>
            <select
              value={chosenCurrency || baseCurrency}
              disabled={updateEnquiry.isPending}
              aria-label="Currency the quotation is built in"
              onChange={(e) => updateEnquiry.mutate({ customer_currency: e.target.value })}
            >
              {quoteCurrencyOptions.map((c) => (
                <option key={c} value={c}>
                  {c}
                  {c === baseCurrency ? ' (company)' : ''}
                </option>
              ))}
            </select>
            <span style={{ color: 'var(--muted)', fontSize: 12 }}>
              {chosenCurrency && chosenCurrency !== baseCurrency
                ? display
                  ? `figures below are shown in ${chosenCurrency}; costs stay in ${baseCurrency} and the quotation is built in ${chosenCurrency}`
                  : `no ${baseCurrency} → ${chosenCurrency} rate on file — figures stay in ${baseCurrency}`
                : `the company's own currency — no conversion`}
            </span>
          </div>

          {quoteFx ? (
            <div
              className="banner"
              style={
                quoteFx.missing || quoteFx.stale
                  ? { background: 'var(--warnbg)', borderColor: 'var(--warn)' }
                  : undefined
              }
            >
              {quoteFx.missing ? (
                <>
                  ⚠ This customer is billed in <b>{quoteFx.to}</b>, and there is{' '}
                  <b>
                    no {baseCurrency} → {quoteFx.to} exchange rate on file
                  </b>{' '}
                  — the quotation cannot be built until one is added.
                </>
              ) : (
                <>
                  💱 Costs are in <b>{baseCurrency}</b>; this customer is quoted in <b>{quoteFx.to}</b> —{' '}
                  <b>{quoteFx.note}</b>
                  {quoteFx.stale ? ' · worth checking before sending' : ''}
                </>
              )}
            </div>
          ) : null}

          <div className="banner">
            🧾 Only <b>RFQ-routed items</b> appear here — provision items on a contract are priced directly and
            reviewed in the{' '}
            <span className="linkish" onClick={() => navigate(`/quote/${id}`)}>
              Customer Quotation
            </span>
            . 🤖 AI strategy sets a starting allocation; then split any line across suppliers to cover partial stock.
            Item column &amp; summary stay pinned. Showing <b>{filtered.length}</b> of {normalised.length} RFQ items.
          </div>

          {/* Before any ranking is trusted: which offers arrived in a different unit, and
              whether they could be restated or had to be dropped. */}
          {/*
            A price that looks like a typo. Stated, never acted on: the offer is still ranked and
            still allocatable, and the message says so — "possible error" otherwise reads as "we
            have already handled it".
          */}
          {outliers.length > 0 ? (
            <div
              className="banner"
              role="alert"
              style={{ background: 'var(--dangerbg)', borderColor: 'var(--dangerline)' }}
            >
              ⚠ {describeOutliers(outliers)}
            </div>
          ) : null}

          {/* What each supplier is being given, biggest first — the question behind every
              negotiation, and previously not answerable from this screen. */}
          {bySupplierTotals.length > 0 ? (
            <div className="note" style={{ marginBottom: 10 }}>
              <b>Allocated per supplier:</b>{' '}
              {bySupplierTotals.map((s, i) => (
                <span key={s.supplier}>
                  {i > 0 ? ' · ' : ''}
                  {s.supplier} <b>{formatMoney(shown(s.total))}</b>
                  <span style={{ color: 'var(--muted)' }}> ({s.lines} line{s.lines === 1 ? '' : 's'})</span>
                </span>
              ))}
            </div>
          ) : null}

          <UomNotesPanel notes={uomNotes} baseCurrency={baseCurrency} />

          <StrategyBar
            active={strategy}
            previews={previews}
            baseCurrency={baseCurrency}
            display={display}
            onPick={applyStrategy}
          />

          <div className="wbtools">
            <div style={{ display: 'flex', gap: 4 }} role="group" aria-label="Layout">
              <button
                type="button"
                className={`chip${view === 'ranked' ? ' on' : ''}`}
                aria-pressed={view === 'ranked'}
                title="Each item lists only the suppliers who quoted it, cheapest first"
                onClick={() => setView('ranked')}
              >
                Ranked
              </button>
              <button
                type="button"
                className={`chip${view === 'matrix' ? ' on' : ''}`}
                aria-pressed={view === 'matrix'}
                title="One column per supplier — the full grid"
                onClick={() => setView('matrix')}
              >
                Matrix
              </button>
            </div>
            {/*
              Stated as what it DOES, not as a mode name. "Single supplier per item" tells a
              purchaser the consequence of ticking a second one; "split mode" would not.
            */}
            <label
              style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', fontSize: 12.5 }}
              title="On: picking a supplier replaces the one already chosen for that item. Off: quantities are split across the suppliers you tick, to cover stock no single supplier holds."
            >
              <input
                type="checkbox"
                checked={soleSupplier}
                onChange={(e) => setSoleSupplier(e.target.checked)}
              />
              One supplier per item
            </label>
            {foreignRows.length > 0 ? (
              <Button
                size="sm"
                onClick={convertAllRates}
                title={`Restate every allocated rate into ${baseCurrency} at the exchange rate on file. The supplier's own figure and currency are kept for the purchase order.`}
              >
                ⇄ Convert {foreignRows.length} rate{foreignRows.length === 1 ? '' : 's'} to {baseCurrency}
              </Button>
            ) : null}
            <input
              type="text"
              placeholder="🔍 Filter items…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              style={{ minWidth: 220 }}
            />
            <span style={{ color: 'var(--muted)', fontSize: 12 }}>Suppliers:</span>
            <div className="suptoggle">
              {supplierCodes.map((supplier) => (
                <label key={supplier}>
                  <input
                    type="checkbox"
                    checked={!hiddenSuppliers.includes(supplier)}
                    onChange={() =>
                      setHiddenSuppliers((current) =>
                        current.includes(supplier)
                          ? current.filter((s) => s !== supplier)
                          : [...current, supplier],
                      )
                    }
                  />
                  {supplierName.get(supplier) ?? supplier}
                </label>
              ))}
            </div>
          </div>

          {pricedDirectly.length > 0 ? (
            <div className="banner" style={{ display: 'grid', gap: 4 }}>
              <div>
                <b>Priced directly — not in the matrix:</b>{' '}
                <span style={{ color: 'var(--muted)' }}>
                  {pricedDirectly.length} line{pricedDirectly.length === 1 ? '' : 's'} from our stock, a contract or a past price. They go on the quotation at these rates.
                </span>
              </div>
              {pricedDirectly.map((line) => (
                <div key={line.line_id} style={{ display: 'flex', gap: 8, alignItems: 'baseline', flexWrap: 'wrap', fontSize: 12.5 }}>
                  <span className="pill">#{line.cust_sno}</span>
                  <span style={{ fontWeight: 600 }}>{itemsByCode.get(line.item_code ?? '')?.name ?? line.item_code}</span>
                  <span>
                    {line.suppliers.includes(IN_HOUSE_SUPPLIER)
                      ? 'our stock'
                      : line.suppliers[0]
                        ? (supplierName.get(line.suppliers[0]) ?? line.suppliers[0])
                        : 'contract'}{' '}
                    @ <b>{formatMoney({ amount: line.selected_rate ?? 0, currency: baseCurrency })}</b>/{line.uom}
                  </span>
                  <span className="linkish" onClick={() => setPricingRow(line.cust_sno)}>
                    change
                  </span>
                  <span
                    className="linkish"
                    title="Put this line back in the comparison, to be priced from the offers"
                    onClick={() => updateLine.mutate({ lineId: line.line_id, patch: { route: 'rfq', selected_rate: 0, suppliers: [] } })}
                  >
                    back to offers
                  </span>
                </div>
              ))}
            </div>
          ) : null}

          {(() => {
            // One set of props for both layouts — they differ in shape, never in behaviour.
            const tableProps: ComponentProps<typeof WorkbenchTable> = {
              items: filtered,
              visibleSuppliers,
              supplierName,
              allocations: working,
              baseCurrency,
              display,
              onToggleCell: handleToggle,
              onSetQty: handleSetQty,
              onAutoCover: handleAutoCover,
              onPriceLine: (rowNo) => setPricingRow(rowNo),
              // The stock badge prices the line in one click (2026-09-18) — `priceFromStock`, the
              // dialog's own write, minus the dialog.
              onUseOurStock: (rowNo) => {
                const line = enquiryLineByRow.get(rowNo)
                const found = line ? ourPriceFor(line) : null
                const patch = found ? priceFromStock(found.amount) : null
                if (!line || !patch) {
                  setPricingRow(rowNo)
                  return
                }
                updateLine.mutate({ lineId: line.line_id, patch })
                toast(`Priced from our own stock at ${found!.amount.toFixed(2)} ${found!.currency}/${found!.uom} — this line leaves the comparison and goes on the quotation at that rate.`)
              },
              inHouseFor: (rowNo) => {
                const line = enquiryLineByRow.get(rowNo)
                if (!line || !line.item_code) return { price: null }
                return { price: ourPriceFor(line), available: stockByCode.get(line.item_code)?.available }
              },
            }
            return view === 'ranked' ? (
              <RankedTable {...tableProps} allItems={normalised} />
            ) : (
              <WorkbenchTable {...tableProps} />
            )
          })()}

          <div className="row" style={{ marginTop: 14, flexWrap: 'wrap' }}>
            <Card className="stat" style={{ flex: 1, minWidth: 150 }}>
              <div className="k">Total ({display?.currency ?? baseCurrency})</div>
              <div className="v">
                <Money value={shown(totals.cost)} />
              </div>
              {display ? (
                <div style={{ fontSize: 11, color: 'var(--muted)' }}>
                  cost {formatMoney({ amount: totals.cost, currency: baseCurrency })}
                </div>
              ) : null}
            </Card>
            <Card className="stat" style={{ flex: 1, minWidth: 150 }}>
              <div className="k">Suppliers used</div>
              <div className="v">{totals.suppliersUsed}</div>
            </Card>
            <Card className="stat" style={{ flex: 1, minWidth: 150 }}>
              <div className="k">Lines covered</div>
              <div className="v">
                {totals.linesCovered}/{totals.lineCount}
              </div>
              {totals.linesCovered < totals.lineCount ? (
                <div className="d">
                  <Badge tone="warn">{totals.lineCount - totals.linesCovered} short</Badge>
                </div>
              ) : null}
            </Card>
            <Card style={{ flex: 1, minWidth: 200, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
              <Button variant="primary" disabled={saving} onClick={buildQuotation}>
                {saving ? 'Saving…' : 'Build customer quotation →'}
              </Button>
              <div style={{ color: 'var(--muted)', fontSize: 11.5, marginTop: 6 }}>
                saves the chosen allocation, then carries it forward
              </div>
            </Card>
          </div>

          {/*
            The minimum-margin gate. Blocks, names what is under, and offers the way through.

            The override is a separate button with its own words — not a styled-primary "OK" — so
            accepting a discount reads as a decision rather than a dismissal. Closing does nothing,
            which is the right default when a purchaser opened this by surprise.
          */}
          {floorPrompt ? (
            <Modal
              title="Below the minimum margin"
              onClose={() => setFloorPrompt(null)}
              width={620}
              footer={
                <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', width: '100%' }}>
                  <Button onClick={() => setFloorPrompt(null)}>Go back and re-price</Button>
                  <Button
                    onClick={() => {
                      const pending = floorPrompt.payload
                      setFloorPrompt(null)
                      // Consumed by this create only — an override must never outlive the decision
                      // that produced it.
                      setOverrideFloor(true)
                      createQuotation.mutate({
                        enquiryId: id,
                        customerReferenceNo: enquiry.header.customer_reference_no,
                        company: enquiry.header.company,
                        enquiryType: enquiry.header.enquiry_type,
                        payload: pending,
                        existing: existingQuotation.data?.name ?? null,
                      })
                    }}
                  >
                    Create anyway — customer discount
                  </Button>
                </div>
              }
            >
              <div style={{ fontSize: 13 }}>
                {describeFloorBreaches(floorPrompt.breaches, marginFloor ?? 0)}
              </div>
              <table style={{ width: '100%', marginTop: 12 }}>
                <thead>
                  <tr>
                    <th style={{ width: 44 }}>#</th>
                    <th>Item</th>
                    <th style={{ width: 110, textAlign: 'right' }}>Margin</th>
                    <th style={{ width: 110, textAlign: 'right' }}>Minimum</th>
                  </tr>
                </thead>
                <tbody>
                  {floorPrompt.breaches.map((b) => (
                    <tr key={b.enquiry_row_no + b.item_code}>
                      <td className="mono">{b.enquiry_row_no}</td>
                      <td>{b.item_name || b.item_code}</td>
                      <td className="mono" style={{ textAlign: 'right', color: 'var(--danger)' }}>
                        {b.pct}%
                      </td>
                      <td className="mono" style={{ textAlign: 'right', color: 'var(--muted)' }}>
                        {marginFloor}%
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <div style={{ color: 'var(--muted)', fontSize: 11.5, marginTop: 10 }}>
                Re-pricing means going back to the workbench and allocating a cheaper supplier, or
                raising the selling price on the quotation. The minimum is set in Settings and can
                be switched off there.
              </div>
            </Modal>
          ) : null}

          {reuseWarning ? (
            <QuotationReuseWarning
              quotation={reuseWarning.quotation}
              changes={reuseWarning.rows.length}
              repricing={setLineCosts.isPending}
              onReprice={() =>
                setLineCosts.mutate(reuseWarning.rows, {
                  onSuccess: () => {
                    toast(`${reuseWarning.rows.length} line(s) re-priced on ${reuseWarning.quotation} — apply the round on the revision page to issue it`)
                    setReuseWarning(null)
                    // `repriced=1`: the revision page opens with the round marked, so "Apply the
                    // round" is offered at once — a reprice-only round has no queued edits.
                    navigate(`/revisions/${id}?repriced=1`)
                  },
                })
              }
              onOpenAnyway={() => {
                setReuseWarning(null)
                navigate(`/revisions/${id}`)
              }}
            />
          ) : null}
        </>
      )}

      {/* The price-this-line dialog — the RFQ page's, verbatim, writing the same enquiry patch. */}
      {pricingRow
        ? (() => {
            const line = enquiryLineByRow.get(pricingRow)
            if (!line || !line.item_code) return null
            const found = ourPriceFor(line)
            return (
              <ItemHistoryModal
                itemCode={line.item_code}
                itemName={itemsByCode.get(line.item_code)?.name ?? line.item_code}
                uom={line.uom}
                available={stockByCode.get(line.item_code)?.available}
                ourPrice={found ? { amount: found.amount, currency: found.currency as Currency } : null}
                contracts={contractsForItem(allRateIndex, line.item_code)}
                rfqSuppliers={line.suppliers}
                onPrice={(patch, label) => {
                  setPricingRow(null)
                  updateLine.mutate({ lineId: line.line_id, patch })
                  toast(`Priced from ${label} — this line leaves the comparison and goes on the quotation at that rate.`)
                }}
                onClose={() => setPricingRow(null)}
              />
            )
          })()
        : null}
      </ReadOnlyGate>
    </>
  )
}
